import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBoxOpen, faReceipt } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import SupervisorHistoryButton from '../common/SupervisorHistoryButton';
import AttachmentsPanel from '../common/AttachmentsPanel';
import CoilLineChips from '../coils/CoilLineChips';

const InfoRow = ({ label, value, isBig = false }) => {
  const { t } = useTranslation();
  const emDash = t('ordersModule.common.symbols.emDash');
  const displayValue = (value !== null && value !== undefined && value !== '') ? value : emDash;

  if (value === null || value === undefined || value === '') return null;

  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>
        {label}
      </span>
      <span className={`font-medium mt-0.5 leading-tight ${isBig ? 'text-base' : 'text-[13px]'}`} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
        {displayValue}
      </span>
    </div>
  );
};

const formatDate = (val, emDash) => {
  if (!val) return emDash;
  try {
    return new Date(val).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return emDash;
  }
};

// `attachmentSubOrderId`: set when the record being viewed is a delivery SUB-ORDER
// (Confirmed Orders page). Its attachments belong to the parent order and are read and
// written through the sub-order attachments route, which resolves the parent
// server-side. Passing a sub-order id as an order id filed uploads under whichever
// unrelated order shared that number.
const OrdersViewModal = ({ isOpen, onClose, order, loading = false, attachmentSubOrderId = null }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const strings = t('ordersModule.orders.view', { returnObjects: true });
  const emDash = t('ordersModule.common.symbols.emDash');

  const orderNumber = order && order.order_number ? order.order_number : strings.header.fallbackTitle;
  const customerName = order && order.customer_name ? order.customer_name : emDash;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <style>{`.orders-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .orders-view-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div className="orders-view-modal w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between border-b px-6 py-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {orderNumber}
            </h2>
            <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{customerName}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto grow space-y-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.loading}</p>
            </div>
          ) : !order ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon icon={faReceipt} className="text-4xl opacity-60" style={{ color: UI_COLORS.TEXT.MUTED }} />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.empty.title}</h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.empty.message}</p>
            </div>
          ) : (
            <>
              {/* Main Info Grid */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-3 md:grid-cols-4 p-3 md:p-4 rounded-xl border bg-gray-50/30 mb-6" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <InfoRow label={strings.fields.orderNumber} value={order.order_number} />
                <InfoRow label={strings.fields.customerName} value={order.customer_name} />
                <InfoRow label={strings.fields.actualQuantity} value={order.actual_quantity} />

                <InfoRow label={strings.fields.date} value={formatDate(order.date, emDash)} />
                <InfoRow label={strings.fields.expectedDeliveryDate} value={formatDate(order.expected_delivery_date, emDash)} />

                <InfoRow label={strings.fields.tallyName} value={order.tally_name} />
                <InfoRow label={strings.fields.deliveryLocation} value={order.delivery_location_name} />

                <InfoRow label={strings.fields.sourceType} value={order.source_type} />
                <InfoRow label={strings.fields.warehouseName} value={order.warehouse_name} />
                <SupervisorHistoryButton label={t('common.formFields.labels.supervisor')} supervisorName={order.supervisor_name} supervisorHistory={order.supervisor_history} />
                <InfoRow label={t('common.formFields.labels.assignedBy')} value={order.assigned_by_name} />
                <InfoRow label={t('common.formFields.labels.orderCreatedBy')} value={order.do_created_by_name} />
                <InfoRow label={t('common.formFields.labels.challanGeneratedBy')} value={order.challan_generated_by_name} />
                <InfoRow label={t('common.formFields.labels.payment')} value={order.payment_terms} />
                <InfoRow
                  label={strings.fields.remark}
                  value={order.remark ? (
                    <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                      {order.remark}
                    </span>
                  ) : null}
                />
                <InfoRow
                  label={t('common.formFields.labels.orderRemark')}
                  value={(order.order_remark || order.remark) ? (
                    <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                      {order.order_remark || order.remark}
                    </span>
                  ) : null}
                />
                <InfoRow
                  label={t('common.formFields.labels.internalRemarks')}
                  value={order.order_internal_remark ? (
                    <span className="block px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                      {order.order_internal_remark}
                    </span>
                  ) : null}
                />
              </div>

              {order && (
                attachmentSubOrderId
                  ? <AttachmentsPanel entityType="order" subOrderId={attachmentSubOrderId} />
                  : <AttachmentsPanel entityType="order" entityId={order.id} />
              )}

              {/* Items Table */}
              <div className="mt-8">
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  <FontAwesomeIcon icon={faBoxOpen} className="text-xs" />
                  {strings.fields.items}
                </h3>


                <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                      <thead>
                        <tr className="border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.1) }}>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">#</th>
                          <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_product}</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Pieces</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_qty}</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Price</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_actual_quantity}</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Load by Pcs</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">{strings.fields.item_remark}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ divideColor: UI_COLORS.BORDERS.LIGHT }}>
                        {Array.isArray(order.items) && order.items.length > 0 ? (
                          order.items.map((it, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                              <td className="px-4 py-4 text-center text-xs font-medium text-gray-400 align-top">
                                {idx + 1}
                              </td>
                              <td className="px-4 py-4 align-top space-y-1">
                                <p className="text-base font-bold text-wrap max-w-xs" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                  {it.size_name || emDash}
                                </p>
                                <div className="flex flex-wrap gap-2 items-center text-wrap max-w-xs">
                                  {it.product_name && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100">
                                      {it.product_name}
                                    </span>
                                  )}
                                  {it.category_name && (
                                    <span className="text-xs font-medium text-gray-500">
                                      {it.category_name}
                                    </span>
                                  )}
                                </div>
                                <CoilLineChips item={it} />
                              </td>
                              {/* Pieces */}
                              <td className="px-4 py-4 text-center align-top">
                                <p className="text-lg font-bold text-blue-600">
                                  {it.quantity_in_pieces || emDash}
                                </p>
                                {it.cut_pieces ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 mt-1 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200" title={t('coils.line.cutPiecesHint')}>
                                    ✂ {t('coils.line.cutPieces')}: {it.cut_pieces}
                                  </span>
                                ) : null}
                              </td>
                              {/* Order Quantity */}
                              <td className="px-4 py-4 text-center align-top">
                                <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                  {(() => {
                                    const q = it.pending_order || it.quantity;
                                    if (!q) return emDash;
                                    return it.unit ? `${q} ${it.unit}` : q;
                                  })()}
                                </p>
                              </td>
                              {/* Price */}
                              <td className="px-4 py-4 text-center align-top">
                                <p className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                  {it.price != null ? `₹${it.price}` : emDash}
                                </p>
                              </td>
                              {/* Actual Quantity */}
                              <td className="px-4 py-4 text-center align-top">
                                <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                  {(() => {
                                    const aq = it.actual_quantity;
                                    if (aq === 0 || aq === 0.0 || aq === '0' || aq === '0.00' || !aq) return emDash;
                                    return it.unit ? `${aq} ${it.unit}` : aq;
                                  })()}
                                </p>
                              </td>
                              {/* Load by Pieces */}
                              <td className="px-4 py-4 text-center align-top">
                                {it.load_by_pieces ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">✓ Pieces</span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              {/* Remark */}
                              <td className="px-4 py-4 text-center align-top min-w-[160px] max-w-[200px]">
                                {it.remark ? (
                                  <span className="block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 text-left" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                                    {it.remark}
                                  </span>
                                ) : emDash}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="px-4 py-12 text-center">
                              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 text-gray-300 mb-3">
                                <FontAwesomeIcon icon={faBoxOpen} className="text-xl" />
                              </div>
                              <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.noItems}</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {strings.buttons.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrdersViewModal;
